import { z } from "zod";

import { idSchema } from "./parcellaire.js";
import { NB_BOUTEILLES_MAX } from "./tirage.js";

const volumeSchema = z.number().finite().min(0).max(1_000_000).multipleOf(0.001);
const sucreSchema = z.number().finite().min(0).max(1_000);
const ml = (litres: number): number => Math.round(litres * 1_000);

/** Lecture de la session : volumes conservés, pas une nouvelle commande. */
export const sourceAppointDetailSchema = z.object({
  id: idSchema,
  type: z.enum(["cuve", "bouteilles"]),
  libelle: z.string(),
  lot_bouteilles_source_id: idSchema.nullable(),
  nb_bouteilles: z.number().int(),
  volume_preleve_l: z.string(),
  volume_incorpore_l: z.string(),
  volume_retour_l: z.string(),
  volume_reliquat_l: z.string(),
  volume_pertes_l: z.string(),
  retour_libelle: z.string().nullable(),
  reliquat_libelle: z.string().nullable(),
  teneur_sucre_g_l: z.string().nullable(),
  justification_sucre: z.string().nullable(),
  origines: z.array(
    z.object({
      lot_id: idSchema,
      libelle: z.string(),
      numerateur: z.string(),
      denominateur: z.string(),
      origine_incertaine: z.boolean(),
      lot_bouteilles_source_id: idSchema.nullable(),
    }),
  ),
});
export type SourceAppointDetail = z.infer<typeof sourceAppointDetailSchema>;

/** Composition physique reconstituée ; les proportions sont relatives à chaque contribution. */
export const compositionResultatDegorgementSchema = z.object({
  volume_final_l: z.string(),
  composition_complete: z.boolean(),
  contributions: z.array(
    z.object({
      role: z.enum(["vin_principal", "appoint", "liqueur_expedition"]),
      source_id: z.string().nullable(),
      libelle: z.string(),
      volume_l: z.string(),
      mode_volume: z.enum(["calcule", "mesure"]),
      origines: z.array(
        z.object({
          source_id: z.string().nullable(),
          type: z.enum(["lot_liquide", "lot_bouteilles", "preparation", "inconnue"]),
          libelle: z.string(),
          numerateur: z.string(),
          denominateur: z.string(),
          origine_incertaine: z.boolean(),
          motif: z.string().nullable(),
          chemin: z.array(z.string()),
        }),
      ),
    }),
  ),
});
export type CompositionResultatDegorgement = z.infer<typeof compositionResultatDegorgementSchema>;

/** Une origine mesurée : aucun reliquat n'est calculé puis recrédité implicitement. */
export const bilanSourceAppointSchema = z
  .object({
    volume_preleve_l: volumeSchema.refine((v) => v > 0, "Indiquez le prélèvement réel."),
    volume_incorpore_l: volumeSchema,
    volume_retour_l: volumeSchema,
    volume_reliquat_l: volumeSchema,
    volume_pertes_l: volumeSchema,
    /** Inconnu n'est jamais assimilé à zéro, notamment pour une donneuse déjà dosée. */
    teneur_sucre_g_l: sucreSchema.nullable(),
  })
  .strict()
  .superRefine((source, ctx) => {
    const destinations =
      ml(source.volume_incorpore_l) +
      ml(source.volume_retour_l) +
      ml(source.volume_reliquat_l) +
      ml(source.volume_pertes_l);
    if (ml(source.volume_preleve_l) !== destinations) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["volume_preleve_l"],
        message: "Le prélèvement doit égaler incorporation + retour + reliquat + pertes.",
      });
    }
  });
export type BilanSourceAppoint = z.infer<typeof bilanSourceAppointSchema>;

const destinationAppointSchema = z.object({
  contenant_id: idSchema,
  version_contenant_attendue: z.string().regex(/^\d+$/),
});

/** Commande préparée : les positions de retour et les filiations sont résolues par le serveur. */
export const sourceAppointDegorgementSchema = z
  .object({
    cle_source: z.string().uuid(),
    source: z.discriminatedUnion("type", [
      z
        .object({
          type: z.literal("cuve"),
          position_id: idSchema,
          version_position_attendue: z.string().regex(/^\d+$/),
        })
        .strict(),
      z
        .object({
          type: z.literal("bouteilles"),
          lot_bouteilles_id: idSchema,
          emplacement_source_id: idSchema.nullable(),
          nb_bouteilles: z.number().int().positive().max(NB_BOUTEILLES_MAX),
        })
        .strict(),
    ]),
    bilan: bilanSourceAppointSchema,
    justification_sucre: z.string().trim().min(1).max(500).nullable(),
    retour: destinationAppointSchema.nullable(),
    reliquat: destinationAppointSchema.nullable(),
    notes: z.string().trim().max(1000).optional(),
  })
  .strict()
  .superRefine((ligne, ctx) => {
    for (const [champ, volume] of [
      ["retour", ligne.bilan.volume_retour_l],
      ["reliquat", ligne.bilan.volume_reliquat_l],
    ] as const) {
      if (volume > 0 !== (ligne[champ] !== null)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [champ],
          message: "Un volume conservé ou retourné exige sa destination physique, et inversement.",
        });
      }
    }
    if ((ligne.bilan.teneur_sucre_g_l !== null) !== (ligne.justification_sucre !== null)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["justification_sucre"],
        message: "La teneur en sucre et sa mesure ou référence doivent être renseignées ensemble.",
      });
    }
  });
export type SourceAppointDegorgement = z.infer<typeof sourceAppointDegorgementSchema>;

export const sourcesAppointDegorgementSchema = z
  .array(sourceAppointDegorgementSchema)
  .min(1)
  .max(64)
  .superRefine((lignes, ctx) => {
    const cles = new Set<string>();
    lignes.forEach((ligne, index) => {
      if (cles.has(ligne.cle_source))
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [index, "cle_source"],
          message: "Ligne d'appoint dupliquée.",
        });
      cles.add(ligne.cle_source);
    });
  });

/**
 * Sucre du vin après remise à niveau, AVANT liqueur d'expédition.
 * Le volume de base est celui restant dans les bouteilles après expulsion :
 * ni BVD, ni retours de doseuse, ni pertes ne contribuent au sucre incorporé.
 * Ce calcul physique ne qualifie aucune mention AOC ou revendication d'origine.
 */
export function calculerSucreApresAppoint(
  volumeVinBaseL: number,
  teneurSucreBaseGL: number | null,
  sources: readonly BilanSourceAppoint[],
): { volume_avant_liqueur_l: number; teneur_sucre_avant_liqueur_g_l: number | null } {
  volumeSchema.parse(volumeVinBaseL);
  sucreSchema.nullable().parse(teneurSucreBaseGL);
  const sourcesValides = sources.map((source) => bilanSourceAppointSchema.parse(source));
  const baseMl = ml(volumeVinBaseL);
  const volumeMl = sourcesValides.reduce((v, s) => v + ml(s.volume_incorpore_l), baseMl);
  const inconnu =
    (baseMl > 0 && teneurSucreBaseGL === null) ||
    sourcesValides.some((s) => s.volume_incorpore_l > 0 && s.teneur_sucre_g_l === null);
  const sucrePondere = sourcesValides.reduce(
    (somme, s) => somme + ml(s.volume_incorpore_l) * (s.teneur_sucre_g_l ?? 0),
    baseMl * (teneurSucreBaseGL ?? 0),
  );
  return {
    volume_avant_liqueur_l: volumeMl / 1_000,
    teneur_sucre_avant_liqueur_g_l: inconnu || volumeMl === 0 ? null : sucrePondere / volumeMl,
  };
}
